import { create } from "zustand";
import toast from "react-hot-toast";
import { RepositoryManager } from "../../data/repositoryManager";

export interface Tag {
    id: number;
    name: string;
    display: boolean;
    created_at: string;
    updated_at: string;
    // Computed properties for UI
    display_name?: string;
    description?: string | null;
    color?: string | null;
    icon?: string | null;
    usage_count?: number;
    is_active?: boolean;
}

interface PaginationLinks {
    url: string | null;
    label: string;
    page: number | null;
    active: boolean;
}

interface TagsDataResponse {
    current_page: number;
    data: Tag[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: PaginationLinks[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
}

interface TagDataResponse {
    data: Tag;
}

interface TagsStoreState {
    tagsData: TagsDataResponse | null;
    tagData: TagDataResponse | null;
    error: string | null;

    isListLoading: boolean;
    isDetailLoading: boolean;
    isSaving: boolean;
    isDeleting: boolean;

    getTags: (params?: any) => Promise<{ success: boolean; data?: TagsDataResponse; error?: string }>;
    getTagById: (tagId: string | number) => Promise<{ success: boolean; data?: TagDataResponse; error?: string }>;
    createTag: (payload: Partial<Tag>) => Promise<{ success: boolean; data?: Tag; error?: string }>;
    updateTag: (tagId: string | number, payload: Partial<Tag>) => Promise<{ success: boolean; data?: Tag; error?: string }>;
    deleteTag: (tagId: string | number) => Promise<{ success: boolean; error?: string }>;
}

const useTagsStore = create<TagsStoreState>((set, get) => ({
    tagsData: null,
    tagData: null,
    error: null,

    isListLoading: false,
    isDetailLoading: false,
    isSaving: false,
    isDeleting: false,

    getTags: async (params = {}) => {
        set({ isListLoading: true, error: null });
        try {
            const result = await RepositoryManager.tags.getTags(params);
            if (result.success && result.data) {
                // Transform API response: data.data + pagination fields at data level
                const transformedData = {
                    current_page: result.data.current_page,
                    data: result.data.data.map((tag: Tag) => ({
                        ...tag,
                        // Add computed properties for UI compatibility
                        display_name: tag.name.charAt(0).toUpperCase() + tag.name.slice(1),
                        is_active: tag.display,
                        usage_count: 0,
                    })),
                    first_page_url: "",
                    from: (result.data.current_page - 1) * result.data.per_page + 1,
                    last_page: result.data.last_page,
                    last_page_url: "",
                    links: [],
                    next_page_url: null,
                    path: "",
                    per_page: result.data.per_page,
                    prev_page_url: null,
                    to: result.data.current_page * result.data.per_page,
                    total: result.data.total,
                };
                set({ tagsData: transformedData, isListLoading: false });
                return { success: true, data: transformedData };
            }
            throw new Error(result.message || "Failed to fetch tags");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            set({ isListLoading: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },

    getTagById: async (tagId) => {
        set({ isDetailLoading: true, error: null });
        try {
            const result = await RepositoryManager.tags.getTagById(tagId);
            if (result.success && result.data) {
                set({ tagData: { data: result.data }, isDetailLoading: false });
                return { success: true, data: { data: result.data } };
            }
            throw new Error(result.message || "Failed to fetch tag details");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            set({ isDetailLoading: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },

    createTag: async (payload) => {
        set({ isSaving: true, error: null });
        try {
            const result = await RepositoryManager.tags.createTag(payload);
            if (result.success && result.data) {
                const newTag = result.data as Tag;
                const { tagsData } = get();
                if (tagsData) {
                    const newData = {
                        ...tagsData,
                        data: [newTag, ...tagsData.data],
                        total: tagsData.total + 1,
                    };
                    set({ tagsData: newData });
                }
                toast.success("Tag created successfully ✅");
                set({ isSaving: false });
                return { success: true, data: newTag };
            }
            throw new Error(result.message || "Failed to create tag");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Error creating tag";
            set({ isSaving: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },

    updateTag: async (tagId, payload) => {
        set({ isSaving: true, error: null });
        try {
            const result = await RepositoryManager.tags.updateTag(tagId, payload);
            if (result.success && result.data) {
                const updatedTag = result.data as Tag;
                const { tagsData } = get();
                if (tagsData) {
                    const newData = {
                        ...tagsData,
                        data: tagsData.data.map((t) => (t.id === updatedTag.id ? updatedTag : t)),
                    };
                    set({ tagsData: newData });
                }
                toast.success("Tag updated successfully ✅");
                set({ isSaving: false });
                return { success: true, data: updatedTag };
            }
            throw new Error(result.message || "Failed to update tag");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Error updating tag";
            set({ isSaving: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },

    deleteTag: async (tagId) => {
        set({ isDeleting: true, error: null });
        try {
            const result = await RepositoryManager.tags.deleteTag(tagId);
            if (result.success) {
                const { tagsData } = get();
                if (tagsData) {
                    set({
                        tagsData: {
                            ...tagsData,
                            data: tagsData.data.filter((t) => t.id !== tagId),
                            total: tagsData.total - 1,
                        },
                    });
                }
                toast.success("Tag deleted successfully 🗑");
                set({ isDeleting: false });
                return { success: true };
            }
            throw new Error(result.message || "Failed to delete tag");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Error deleting tag";
            set({ isDeleting: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },
}));

export default useTagsStore;
